/**
 * Hook for file uploads with progress tracking and schema preview.
 *
 * In cloud environments (GCS backend):
 *   - Client uploads directly to GCS via resumable upload URLs
 *   - No file bytes pass through the backend
 *
 * In local development (local backend):
 *   - Client uploads via the local-upload endpoint
 *
 * The hook API is unchanged — components don't need to know the storage backend.
 */

import { useState, useCallback, useRef } from "react";
import {
  createUploadSession,
  completeUpload,
  cancelUploadSession,
  inferSchemaFromPreview,
  approveSchema as approveSchemaSDK,
} from "../sdk";
import {
  uploadToGCSResumable,
  uploadToLocalBackend,
  type GCSUploadProgress,
} from "../utils/gcs-upload";

// Re-export SDK types for backwards compatibility
export type { SchemaColumn, InferredSchema } from "../sdk";

import type { InferredSchema } from "../sdk";

// Helper to extract error message from SDK response
function getErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;
  if (typeof error.detail === "string") return error.detail;
  if (Array.isArray(error.detail) && error.detail.length > 0) {
    return error.detail.map((e: any) => e.msg || String(e)).join(", ");
  }
  if (error.message) return error.message;
  return fallback;
}

interface UploadSession {
  session_id: string;
  expires_at: string;
  resumable_url: string | null;
  preview_resumable_url: string | null;
  gcs_preview_path: string | null;
  enable_schema_preview?: boolean;
  preview_size_bytes?: number;
  phase?: string;
  storage_backend: string;
}

// Schema inference response
interface SchemaInferenceResult {
  session_id: string;
  inferred_schema: InferredSchema;
  preview_data: {
    rows: number;
    sample: Record<string, string>[];
  };
  recommendations: string[];
  warnings: string[];
  phase: string;
}

export interface SchemaInferenceOptions {
  sampleRows?: number;
  sheetName?: string | null;
  headerRow?: number | null;
}

export interface SchemaApprovalOptions {
  sheetName?: string | null;
  headerRow?: number | null;
  ingestAllSheets?: boolean;
}

// Upload phases
type UploadPhase =
  | "initializing"
  | "uploading" // Standard uploading (no schema preview)
  | "preview_uploading" // Uploading preview slice
  | "schema_inferring" // Inferring schema from preview
  | "awaiting_approval" // Waiting for user to approve schema
  | "full_uploading" // Uploading full file after approval
  | "completing"
  | "processing"
  | "completed"
  | "failed";

interface UploadProgress {
  uploadedBytes: number;
  totalBytes: number;
  progress: number;
  currentPhase: UploadPhase;
  estimatedTimeRemaining?: number;
  // Schema preview specific
  inferredSchema?: InferredSchema;
  previewData?: Record<string, string>[];
  schemaRecommendations?: string[];
  schemaWarnings?: string[];
}

interface CompleteUploadResult {
  session_id: string;
  file_id: string;
  file_path: string;
  job_id: string;
  status: string;
  message: string;
}

interface UseChunkedUploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onComplete?: (result: CompleteUploadResult) => void;
  onError?: (error: Error) => void;
  // Schema preview options
  enableSchemaPreview?: boolean;
  previewSizeBytes?: number; // Default: 50MB
  previewChunks?: number; // Legacy option: 1 chunk ~= 5MB
  onSchemaInferred?: (result: SchemaInferenceResult) => void;
  onSchemaApproved?: () => void;
  // Auth headers for local-upload fallback
  getHeaders?: () => Promise<Record<string, string>>;
  baseUrl?: string;
}

export function useChunkedUpload(options: UseChunkedUploadOptions = {}) {
  const {
    onProgress,
    onComplete,
    onError,
    enableSchemaPreview = false,
    previewSizeBytes = 50 * 1024 * 1024,
    previewChunks,
    onSchemaInferred,
    onSchemaApproved,
    getHeaders,
    baseUrl = "",
  } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  const [inferredSchema, setInferredSchema] = useState<InferredSchema | null>(
    null,
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const startTimeRef = useRef<number>(0);
  const sessionRef = useRef<UploadSession | null>(null);
  const fileRef = useRef<File | null>(null);
  const effectivePreviewSizeBytes =
    typeof previewChunks === "number" && previewChunks > 0
      ? previewChunks * 5 * 1024 * 1024
      : previewSizeBytes;

  const updateProgress = useCallback(
    (updates: Partial<UploadProgress>) => {
      setProgress((prev) => {
        const next = { ...(prev as UploadProgress), ...updates };
        onProgress?.(next);
        return next;
      });
    },
    [onProgress],
  );

  const uploadFile = useCallback(
    async (
      file: File,
      orgId: string,
      namespace: string = "default",
      inferenceOptions?: SchemaInferenceOptions,
    ) => {
      setIsUploading(true);
      setError(null);
      startTimeRef.current = Date.now();
      abortControllerRef.current = new AbortController();

      try {
        // Initialize progress
        const initialProgress: UploadProgress = {
          uploadedBytes: 0,
          totalBytes: file.size,
          progress: 0,
          currentPhase: "initializing",
        };
        setProgress(initialProgress);
        onProgress?.(initialProgress);

        // Step 1: Create upload session
        const sessionResponse = await createUploadSession({
          body: {
            file_name: file.name,
            file_size: file.size,
            content_type: file.type || "application/octet-stream",
            org_id: orgId,
            namespace,
            enable_schema_preview: enableSchemaPreview,
            preview_size_bytes: effectivePreviewSizeBytes,
          },
        });

        if (sessionResponse.error) {
          throw new Error(
            getErrorMessage(
              sessionResponse.error,
              "Failed to create upload session",
            ),
          );
        }

        const session = sessionResponse.data as UploadSession;
        sessionRef.current = session;
        fileRef.current = file;

        const isLocal = session.storage_backend === "local";

        // Step 2: Upload the file
        if (enableSchemaPreview) {
          // Schema preview flow: upload preview slice first
          updateProgress({ currentPhase: "preview_uploading" });

          const previewSizeFromSession =
            session.preview_size_bytes && session.preview_size_bytes > 0
              ? session.preview_size_bytes
              : effectivePreviewSizeBytes;
          const previewSlice = file.slice(
            0,
            Math.min(previewSizeFromSession, file.size),
          );

          if (isLocal) {
            const headers = getHeaders ? await getHeaders() : {};
            await uploadToLocalBackend(
              baseUrl,
              session.session_id,
              new File([previewSlice], file.name, { type: file.type }),
              headers,
              (p) =>
                updateProgress({
                  uploadedBytes: p.uploaded,
                  progress: (p.percentage / 100) * 50,
                }),
              abortControllerRef.current.signal,
            );
          } else if (session.preview_resumable_url) {
            await uploadToGCSResumable(
              session.preview_resumable_url,
              previewSlice,
              previewSlice.size,
              (p) =>
                updateProgress({
                  uploadedBytes: p.uploaded,
                  progress: (p.percentage / 100) * 50,
                }),
              abortControllerRef.current.signal,
            );
          } else {
            throw new Error("No preview upload URL available");
          }

          // Step 3: Infer schema
          updateProgress({ currentPhase: "schema_inferring" });

          const schemaResponse = await inferSchemaFromPreview({
            path: { session_id: session.session_id },
            body: {
              sample_rows: inferenceOptions?.sampleRows ?? 10000,
              sheet_name: inferenceOptions?.sheetName ?? undefined,
              header_row: inferenceOptions?.headerRow ?? undefined,
            },
          });

          if (schemaResponse.error) {
            throw new Error(
              getErrorMessage(schemaResponse.error, "Schema inference failed"),
            );
          }

          const schemaResult = schemaResponse.data as SchemaInferenceResult;

          setInferredSchema(schemaResult.inferred_schema);
          updateProgress({
            currentPhase: "awaiting_approval",
            progress: 50,
            inferredSchema: schemaResult.inferred_schema,
            previewData: schemaResult.preview_data.sample,
            schemaRecommendations: schemaResult.recommendations,
            schemaWarnings: schemaResult.warnings,
          });
          setIsAwaitingApproval(true);

          onSchemaInferred?.(schemaResult);

          // Return — user calls approveSchemaAndContinue to proceed
          return { session_id: session.session_id, awaiting_approval: true };
        } else {
          // No schema preview: upload full file directly
          updateProgress({ currentPhase: "uploading" });

          if (isLocal) {
            const headers = getHeaders ? await getHeaders() : {};
            await uploadToLocalBackend(
              baseUrl,
              session.session_id,
              file,
              headers,
              (p) =>
                updateProgress({
                  uploadedBytes: p.uploaded,
                  progress: p.percentage,
                }),
              abortControllerRef.current.signal,
            );
          } else if (session.resumable_url) {
            await uploadToGCSResumable(
              session.resumable_url,
              file,
              file.size,
              (p) =>
                updateProgress({
                  uploadedBytes: p.uploaded,
                  progress: p.percentage,
                }),
              abortControllerRef.current.signal,
            );
          } else {
            throw new Error("No upload URL available");
          }
        }

        // Step 3: Complete upload
        updateProgress({ currentPhase: "completing" });

        const completeResponse = await completeUpload({
          path: { session_id: session.session_id },
          body: { session_id: session.session_id },
        });

        if (completeResponse.error) {
          throw new Error(
            getErrorMessage(
              completeResponse.error,
              "Failed to complete upload",
            ),
          );
        }

        const result = completeResponse.data as CompleteUploadResult;

        updateProgress({ currentPhase: "processing", progress: 100 });
        onComplete?.(result);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        onError?.(error);
        updateProgress({ currentPhase: "failed" });
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [
      enableSchemaPreview,
      effectivePreviewSizeBytes,
      onProgress,
      onComplete,
      onError,
      onSchemaInferred,
      getHeaders,
      baseUrl,
      updateProgress,
    ],
  );

  const cancelUpload = useCallback(
    async (sessionId?: string) => {
      abortControllerRef.current?.abort();

      const sid = sessionId || sessionRef.current?.session_id;
      if (sid) {
        try {
          await cancelUploadSession({
            path: { session_id: sid },
            query: { cleanup: true },
          });
        } catch (err) {
          console.error("Failed to cancel upload session:", err);
        }
      }

      setIsUploading(false);
      setIsAwaitingApproval(false);
      updateProgress({ currentPhase: "failed" });
    },
    [updateProgress],
  );

  /**
   * Approve the inferred schema and continue uploading the full file.
   * Call this after receiving the onSchemaInferred callback.
   */
  const approveSchemaAndContinue = useCallback(
    async (
      approvedSchema: InferredSchema,
      tableName: string,
      operation: "create" | "append" | "replace" = "create",
      schemaMapping?: {
        columnMappings: Array<{
          source_column: string;
          target_column: string;
        }>;
        excludedColumns?: string[];
      },
      approvalOptions?: SchemaApprovalOptions,
    ) => {
      if (!sessionRef.current || !fileRef.current) {
        throw new Error("No active upload session");
      }

      const session = sessionRef.current;
      const file = fileRef.current;

      try {
        // Approve schema on backend — creates job + full-file resumable URL
        const approveResponse = await approveSchemaSDK({
          path: { session_id: session.session_id },
          body: {
            approved_schema: approvedSchema,
            table_name: tableName,
            operation,
            column_mappings: schemaMapping?.columnMappings,
            excluded_columns: schemaMapping?.excludedColumns,
            sheet_name: approvalOptions?.sheetName ?? undefined,
            header_row: approvalOptions?.headerRow ?? undefined,
            ingest_all_sheets: approvalOptions?.ingestAllSheets ?? false,
          },
        });

        if (approveResponse.error) {
          throw new Error(
            getErrorMessage(approveResponse.error, "Schema approval failed"),
          );
        }

        const approveResult = approveResponse.data as {
          session_id: string;
          phase: string;
          resumable_url: string | null;
          job_id: string;
          storage_backend: string;
        };

        setIsAwaitingApproval(false);
        onSchemaApproved?.();

        // Notify onComplete immediately so UI can navigate to job tracking
        const earlyResult: CompleteUploadResult = {
          session_id: session.session_id,
          file_id: "",
          file_path: "",
          job_id: approveResult.job_id,
          status: "uploading",
          message: "Upload continuing in background",
        };
        onComplete?.(earlyResult);

        // Upload full file in background (fire and forget)
        const continueInBackground = async () => {
          try {
            updateProgress({ currentPhase: "full_uploading" });

            const isLocal = approveResult.storage_backend === "local";

            if (isLocal) {
              const headers = getHeaders ? await getHeaders() : {};
              await uploadToLocalBackend(
                baseUrl,
                session.session_id,
                file,
                headers,
                (p) =>
                  updateProgress({
                    uploadedBytes: p.uploaded,
                    progress: 50 + (p.percentage / 100) * 50,
                  }),
                abortControllerRef.current?.signal,
              );
            } else if (approveResult.resumable_url) {
              await uploadToGCSResumable(
                approveResult.resumable_url,
                file,
                file.size,
                (p) =>
                  updateProgress({
                    uploadedBytes: p.uploaded,
                    progress: 50 + (p.percentage / 100) * 50,
                  }),
                abortControllerRef.current?.signal,
              );
            }

            // Complete upload
            updateProgress({ currentPhase: "completing" });

            const completeResponse = await completeUpload({
              path: { session_id: session.session_id },
              body: { session_id: session.session_id },
            });

            if (completeResponse.error) {
              console.error(
                "Background upload completion failed:",
                completeResponse.error,
              );
            }

            updateProgress({ currentPhase: "processing", progress: 100 });
          } catch (err) {
            console.error("Background upload failed:", err);
          } finally {
            setIsUploading(false);
          }
        };

        continueInBackground();
        return earlyResult;
      } catch (err) {
        const error = err as Error;
        setError(error);
        onError?.(error);
        updateProgress({ currentPhase: "failed" });
        setIsUploading(false);
        throw error;
      }
    },
    [
      onComplete,
      onError,
      onSchemaApproved,
      getHeaders,
      baseUrl,
      updateProgress,
    ],
  );

  return {
    uploadFile,
    cancelUpload,
    approveSchemaAndContinue,
    isUploading,
    isAwaitingApproval,
    inferredSchema,
    progress,
    error,
    session: sessionRef.current,
  };
}
